import { MapPin, Bed, Ruler, Image as ImageIcon, BadgeCheck } from "lucide-react";
import { fileUrl, formatTL } from "@/lib/api";
import { STATUS_LABEL } from "@/lib/portfolioOptions";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const Thumb = ({ p, className }) => (
  <div className={`bg-slate-100 overflow-hidden flex items-center justify-center ${className}`}>
    {p.image_paths?.[0] ? <img src={fileUrl(p.image_paths[0], "md")} loading="lazy" alt={p.title} className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-300" />}
  </div>
);

const TypeBadge = ({ p }) => (
  <Badge className={p.listing_type === "SALE" ? "bg-emerald-600" : "bg-blue-600"}>{p.listing_type === "SALE" ? "Satılık" : "Kiralık"}</Badge>
);

export const PortfolioList = ({ items, onOpen }) => (
  <Card className="overflow-x-auto" data-testid="portfolio-list-view">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
        <tr>{["", "İlan", "Tip", "Oda / m²", "Kat", "Konum", "Fiyat", "Durum", "Danışman"].map((h, i) => <th key={i} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>)}</tr>
      </thead>
      <tbody>
        {items.map((p) => (
          <tr key={p.id} data-testid={`portfolio-row-${p.id}`} onClick={() => onOpen(p)} className="border-t cursor-pointer hover:bg-slate-50 transition-colors">
            <td className="px-4 py-2"><Thumb p={p} className="w-16 h-12 rounded-md" /></td>
            <td className="px-4 py-2 max-w-[240px]">
              <div className="font-semibold text-slate-900 truncate">{p.title}</div>
              {p.has_eids && <div className="text-[11px] text-emerald-700 flex items-center gap-1"><BadgeCheck className="w-3 h-3" />EİDS {p.eids_code}</div>}
            </td>
            <td className="px-4 py-2"><TypeBadge p={p} /></td>
            <td className="px-4 py-2 whitespace-nowrap">{p.rooms} · {p.area_m2} m²</td>
            <td className="px-4 py-2 whitespace-nowrap text-slate-600">{p.floor || "-"}</td>
            <td className="px-4 py-2 whitespace-nowrap text-slate-600">{p.district}, {p.city}{p.lat != null && <MapPin className="inline w-3 h-3 ml-1 text-emerald-600" />}</td>
            <td className="px-4 py-2 whitespace-nowrap font-bold text-emerald-700">{formatTL(p.price)}</td>
            <td className="px-4 py-2 whitespace-nowrap text-xs">{STATUS_LABEL[p.status]}</td>
            <td className="px-4 py-2 whitespace-nowrap text-xs text-slate-500">{p.owner_name}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </Card>
);

export const PortfolioGrid = ({ items, onOpen }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="portfolio-grid-view">
    {items.map((p) => (
      <Card key={p.id} data-testid={`portfolio-card-${p.id}`} className="overflow-hidden cursor-pointer hover:shadow-xl transition-shadow group" onClick={() => onOpen(p)}>
        <div className="aspect-video relative overflow-hidden">
          <Thumb p={p} className="w-full h-full group-hover:scale-105 transition-transform" />
          <div className="absolute top-3 left-3"><TypeBadge p={p} /></div>
          {p.has_eids && <Badge variant="outline" className="absolute top-3 right-3 bg-white/95 border-emerald-400 text-emerald-700">EİDS</Badge>}
        </div>
        <div className="p-5">
          <div className="font-semibold text-slate-900 mb-1 line-clamp-1" style={{ fontFamily: "Outfit, sans-serif" }}>{p.title}</div>
          <div className="text-xs text-slate-500 flex items-center gap-1 mb-3"><MapPin className="w-3 h-3" /> {p.district}, {p.city}</div>
          <div className="flex items-center gap-3 text-xs text-slate-600 mb-3">
            <span className="flex items-center gap-1"><Bed className="w-3 h-3" /> {p.rooms}</span>
            <span className="flex items-center gap-1"><Ruler className="w-3 h-3" /> {p.area_m2}m²</span>
            {p.floor && <span>{p.floor}</span>}
          </div>
          <div className="flex items-end justify-between">
            <div className="text-xl font-bold text-emerald-700" style={{ fontFamily: "Outfit, sans-serif" }}>{formatTL(p.price)}</div>
            <div className="text-[10px] text-slate-400">{p.owner_name}</div>
          </div>
        </div>
      </Card>
    ))}
  </div>
);
